import { useCallback, useEffect, useState } from "react";
import { Phone, PhoneOutgoing, Loader2, Settings2, ShieldCheck, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { VoiceCallCard } from "../components/VoiceCallCard";
import { VoiceAgentPicker } from "../components/VoiceAgentPicker";
import { ConfirmDialog } from "../components/ConfirmDialog";

const POLL_MS = 8000;

export default function Chiamate() {
  const [status, setStatus] = useState(null);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ numero: "", nome: "", note: "" });
  const [calling, setCalling] = useState(false);
  const [lastCall, setLastCall] = useState(null);
  const [settingUp, setSettingUp] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const doDelete = async () => {
    try {
      await api.delete(`/voice/calls/${toDelete.call_id}`);
      setCalls((cs) => cs.filter((c) => c.call_id !== toDelete.call_id));
      toast.success("Chiamata eliminata");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setToDelete(null);
    }
  };

  const load = useCallback(async (silent = false) => {
    try {
      const [s, c] = await Promise.all([api.get("/voice/status"), api.get("/voice/calls")]);
      setStatus(s.data);
      setCalls(c.data.items);
    } catch (e) {
      if (!silent) toast.error(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Polling leggero finché ci sono chiamate ancora attive (il webhook aggiorna il DB)
  const hasActive = calls.some((c) => c.stato !== "ended");
  useEffect(() => {
    if (!hasActive) return;
    const t = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(t);
  }, [hasActive, load]);

  const startCall = async (e) => {
    e.preventDefault();
    if (!form.numero.trim()) return toast.error("Inserisci il numero da chiamare");
    setCalling(true);
    try {
      const { data } = await api.post("/voice/outbound", { numero: form.numero.trim(), nome: form.nome.trim() || null, note: form.note.trim() || null });
      setLastCall(data);
      toast.success("Chiamata avviata");
      setForm({ numero: "", nome: "", note: "" });
      await load(true);
    } catch (e2) {
      toast.error(errorMessage(e2));
    } finally {
      setCalling(false);
    }
  };

  const runSetup = async () => {
    setSettingUp(true);
    try {
      const { data } = await api.post("/voice/setup");
      toast.success(`Assistente ${data.creato ? "creato" : "aggiornato"} e assegnato al numero ${data.numero || ""}`);
      (data.avvisi || []).forEach((a) => toast.warning(a, { duration: 12000 }));
      await load(true);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSettingUp(false);
    }
  };

  const configured = status?.configurato;
  const ready = configured && !!status?.assistant_id;

  return (
    <div className="space-y-8" data-testid="chiamate-page">
      <div className="fade-up">
        <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Chiamate vocali AI</p>
        <h1 className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl">Chiamate</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          L'assistente vocale AI di {status?.azienda || "SalesPailot"} risponde alle chiamate in entrata e può effettuare chiamate di primo contatto.
          Il parlato in tempo reale è gestito da Vapi; qui trovi il form per le chiamate in uscita e lo storico con trascrizione e audio.
        </p>
      </div>

      {/* Stato configurazione */}
      {status && (
        <section data-testid="voice-status-box" className={`surface flex flex-wrap items-center justify-between gap-4 p-5 ${ready ? "" : "border-amber-500/40"}`}>
          <div className="flex items-start gap-3">
            {ready ? <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-300" /> : <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-300" />}
            <div className="text-sm">
              {!configured ? (
                <p data-testid="voice-status-missing">Configurazione mancante: imposta <span className="font-mono text-xs">{status.mancanti.join(", ")}</span> nel .env del backend.</p>
              ) : ready ? (
                <p data-testid="voice-status-ready">Assistente attivo sul numero <span className="font-semibold text-white">{status.numero || status.assistant_id}</span>. Le chiamate in entrata ricevono la risposta dell'AI.</p>
              ) : (
                <p data-testid="voice-status-nosetup">Chiavi presenti ma assistente non ancora creato: {status.is_admin ? "esegui il setup." : "chiedi all'amministratore di eseguire il setup."}</p>
              )}
              {status.ultimo_setup && <p className="mt-1 text-xs text-muted-foreground">Ultimo setup: {new Date(status.ultimo_setup).toLocaleString("it-IT")}</p>}
              {ready && status.is_admin && (
                <p data-testid="voice-agent-voice" className="mt-1 text-xs text-muted-foreground">
                  Voce dell'agente: {status.voce_agente ? <span className="text-[#FCD990]">{status.voce_agente.nome}{status.voce_agente.tipo === "preset" ? " (predefinita)" : " (clonata da MyPailot)"}</span> : <>voce di default</>}
                </p>
              )}
            </div>
          </div>
          {status.is_admin && configured && (
            <button data-testid="voice-setup-btn" onClick={runSetup} disabled={settingUp} className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-xs font-semibold text-white hover:bg-secondary disabled:opacity-50">
              {settingUp ? <Loader2 className="h-4 w-4 spin-slow" /> : <Settings2 className="h-4 w-4" />} {ready ? "Aggiorna assistente" : "Configura assistente"}
            </button>
          )}
        </section>
      )}

      {/* Voce delle chiamate (solo admin) */}
      {status?.is_admin && ready && <VoiceAgentPicker onChange={() => load(true)} />}

      {/* Outbound */}
      <section className="fade-up surface space-y-4 p-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/15 text-primary"><PhoneOutgoing className="h-5 w-5" /></div>
          <div>
            <h2 className="text-base font-semibold md:text-lg">Chiamata in uscita</h2>
            <p className="text-xs text-muted-foreground">L'assistente si presenta sempre come AI (apertura obbligatoria, non disattivabile).</p>
          </div>
        </div>
        <form onSubmit={startCall} className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground">Numero (internazionale)</label>
            <input data-testid="outbound-numero-input" value={form.numero} onChange={(e) => setForm((f) => ({ ...f, numero: e.target.value }))} placeholder="+39 333 1234567" className="w-full rounded-lg border border-input bg-background px-4 py-3 text-sm outline-none focus:border-primary" />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground">Nome (opzionale)</label>
            <input data-testid="outbound-nome-input" value={form.nome} onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))} placeholder="Es. Mario Rossi" className="w-full rounded-lg border border-input bg-background px-4 py-3 text-sm outline-none focus:border-primary" />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground">Note per l'assistente (opzionale)</label>
            <textarea data-testid="outbound-note-input" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} rows={2} placeholder="Es. ha chiesto informazioni sul sito, interessato al piano Business" className="w-full rounded-lg border border-input bg-background px-4 py-3 text-sm outline-none focus:border-primary" />
          </div>
          <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
            <button type="submit" data-testid="outbound-start-btn" disabled={calling || !ready} className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#1D4ED8] disabled:opacity-50">
              {calling ? <Loader2 className="h-4 w-4 spin-slow" /> : <Phone className="h-4 w-4" />} Avvia chiamata
            </button>
            {lastCall && (
              <p data-testid="outbound-result" className="text-xs text-muted-foreground">
                Stato: <span className="font-semibold text-emerald-300">{lastCall.stato}</span> · callId <span className="font-mono">{lastCall.callId}</span>
              </p>
            )}
          </div>
        </form>
      </section>

      {/* Storico */}
      <section className="fade-up">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold md:text-lg">Ultime chiamate {calls.length > 0 && <span className="text-muted-foreground">({calls.length})</span>}</h2>
          <button data-testid="calls-refresh-btn" onClick={() => load()} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground hover:text-white"><RefreshCw className="h-3.5 w-3.5" /> Aggiorna</button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 spin-slow text-primary" /></div>
        ) : calls.length === 0 ? (
          <div data-testid="calls-empty" className="surface p-10 text-center text-sm text-muted-foreground">Nessuna chiamata ancora. Avvia la prima qui sopra oppure chiama il numero dell'assistente.</div>
        ) : (
          <ul data-testid="calls-list" className="space-y-3">
            {calls.map((c) => <VoiceCallCard key={c.call_id} call={c} showOwner={status?.is_admin} onDelete={setToDelete} />)}
          </ul>
        )}
      </section>

      <ConfirmDialog open={!!toDelete} title="Eliminare questa chiamata?" description={`La chiamata ${toDelete?.direzione === "inbound" ? "in entrata da" : "in uscita verso"} ${toDelete?.numero || ""} verrà rimossa dallo storico (trascrizione e riassunto inclusi).`} onCancel={() => setToDelete(null)} onConfirm={doDelete} confirmLabel="Elimina" />
    </div>
  );
}
